<script setup>
  import {computed, nextTick, ref} from "vue";

  import ComponentDialog from "@components/Dialogs/Dialog.vue";
  import ComponentRecordShareCompleteDialog from "@components/Dialogs/RecordShareCompleteDialog.vue";
  import ComponentRecordShareRevokeDialog from "@components/Dialogs/RecordShareRevokeDialog.vue";
  import ComponentIcon from "@components/Icons/Icon.vue";
  import {router, usePage} from "@inertiajs/vue3";
  import {recordNotifications, sharedrecordStatus} from "@pages/Records/Enums/Enums.js";
  import {MedButton, MedTooltip} from "@pharma4u/medicheck-components";

  defineOptions({
    name: "ComponentRecordShareStatusDialog",
  });

  const props = defineProps({
    patient: {
      type: Object,
      required: true,
    },
    record: {
      type: [Object, null],
      default: null,
    },
    sharedata: {
      type: Object,
      default: null,
    },
    notifications: {
      type: Object,
      default: null,
    },
  });

  const refDialog = ref(null);
  const refList = ref(null);
  const refConfirmRevokeDialog = ref(null);
  const refConfirmCompleteDialog = ref(null);

  const currentRecord = ref(props.record);
  const currentSharedata = ref(props.sharedata);
  const currentNotifications = ref(props.notifications);

  const canCompleteRecord = computed(() => {
    if (!currentSharedata.value.can_complete_this_record) {
      return false;
    }

    if (
      !currentSharedata.value.shared_owners ||
      (currentSharedata.value.shared_owners.length === 0) === 0 ||
      !currentSharedata.value.shared_owners[0]?.id
    ) {
      return false;
    }

    const typeOfFirstNonCommentNotification = (currentNotifications.value ?? []).find(
      (notification) => notification.type !== recordNotifications.commented,
    )?.type;

    if (
      typeOfFirstNonCommentNotification === recordNotifications.takenAwayFromMe ||
      typeOfFirstNonCommentNotification === recordNotifications.completedFromMe
    ) {
      // disable "complete" button, when the latest notification for
      // this record is takenAwayFromMe or completedFromMe
      return false;
    }

    return true;
  });

  const ownerName = computed(() => {
    return currentSharedata.value?.shared_owners[0]?.owner_name;
  });

  const ownerCompany = computed(() => {
    return currentSharedata.value?.shared_owners[0]?.owner_company;
  });

  function open(record) {
    if (record !== undefined) {
      currentRecord.value = record;
      if (record.sharedata !== undefined) {
        currentSharedata.value = record.sharedata;
      }
      if (record.notifications !== undefined) {
        currentNotifications.value = record.notifications;
      }
    }

    // always refresh on open
    refresh();
    refDialog.value.open();
    calculateScrollHeight();
  }

  /**
   * Calculate the max inner height of the scrollable <ul> list to
   * avoid scrolling the whole <dialog> and to visually fake
   * a "fixed" box containing the "send back" button.
   */
  function calculateScrollHeight() {
    nextTick(() => {
      refList.value.style.height = "auto";
      if (refDialog.value.$el.scrollHeight > refDialog.value.$el.clientHeight) {
        refList.value.style.height =
          refList.value.scrollHeight -
          (refDialog.value.$el.scrollHeight - refDialog.value.$el.clientHeight) -
          20 +
          "px";
      }
    });
  }

  function refresh() {
    setTimeout(() => {
      router.reload({
        only: [
          "records", // << on patients/show
          "record",
          "sharedata",
          "notifications", // << on record/edit and record/show
        ],
        preserveState: true,
        preserveScroll: true,
        onSuccess: () => {
          // because we received the currentRecord via the
          // self::open() method, we do not recognize
          // changes after a record has been revoked
          // or completed. therefore we have to
          // search for our record in the page
          // props and refresh it
          const pageProperties = usePage().props;

          // Patients/Show contains the records in "records.data"
          if (pageProperties.records !== undefined && pageProperties.records !== null && pageProperties.records.data) {
            currentRecord.value =
              pageProperties.records.data.find((record) => record.id === currentRecord.value.id) || currentRecord.value;

            if (currentRecord.value.sharedata !== undefined) {
              currentSharedata.value = currentRecord.value.sharedata;
            }
            if (currentRecord.value.notifications !== undefined) {
              currentNotifications.value = currentRecord.value.notifications;
            }
          }
          // check result page contains the record in "record"
          else {
            if (pageProperties.record !== undefined && pageProperties.record !== null) {
              currentRecord.value = pageProperties.record;
            }
            if (pageProperties.sharedata !== undefined && pageProperties.sharedata !== null) {
              currentSharedata.value = pageProperties.sharedata;
            }
            if (pageProperties.notifications !== undefined && pageProperties.notifications !== null) {
              currentNotifications.value = pageProperties.notifications.data;
            }
          }
        },
      });
    }, 600);
  }

  function close() {
    refDialog.value.close();
  }

  function getActionIcon(notification) {
    switch (notification.type) {
      case recordNotifications.sharedByMe:
        return "call_made";
      case recordNotifications.sharedWithMe:
        return "call_received";
      case recordNotifications.takenAwayFromMe:
        return "person_add_disabled";
      case recordNotifications.revokedByMe:
        return "person_add_disabled";
      case recordNotifications.completedFromMe:
        return "done";
      case recordNotifications.returnedToMe:
        return "keyboard_backspace";
      case recordNotifications.editedFromMe:
        return "edit";
      case recordNotifications.commented:
        return notification.confidential ? "custom:doctor" : "mode_comment";
      // No default
    }
  }

  function getAliasName(notification) {
    return notification.aliasname ? `„${notification.aliasname}“` : "";
  }

  function getIntroText(notification, separate = false) {
    let parts = [];
    if (notification.type === recordNotifications.sharedByMe && notification.audience === "group")
      parts = ["Sie haben für diese Variante eine Seminar-Freigabe", "erstellt"];
    else
      switch (notification.type) {
        case recordNotifications.sharedByMe: {
          parts = ["Sie haben diese Variante", "geteilt mit"];
          break;
        }
        case recordNotifications.sharedWithMe: {
          parts = ["Diese Variante", "wurde mit Ihnen geteilt von"];
          break;
        }
        case recordNotifications.takenAwayFromMe: {
          parts = ["Freigabe", "wurde widerrufen von"];
          break;
        }
        default:
          if (notification.type === recordNotifications.revokedByMe && notification.audience === "group")
            parts = ["Sie haben die Seminar-Freigabe", "widerrufen"];
          else
            switch (notification.type) {
              case recordNotifications.revokedByMe: {
                parts = ["Sie haben die Freigabe", "widerrufen gegenüber"];
                break;
              }
              case recordNotifications.completedFromMe: {
                parts = ["Sie haben diese Variante", "zurückgesendet an"];
                break;
              }
              case recordNotifications.returnedToMe: {
                if (notification.with_changes) {
                  parts = ["Diese Variante", "wurde mit inhaltlichen Änderungen an Sie zurückgesendet von"];
                } else {
                  parts = ["Diese Variante", "wurde an Sie zurückgesendet von"];
                }

                break;
              }
              case recordNotifications.editedFromMe: {
                parts = ["Sie haben Änderungen an dieser Variante", "vorgenommen"];

                break;
              }
              // No default
            }
      }

    return separate ? parts : parts.join(" ");
  }

  function getVerbalizedActionText(notification) {
    switch (notification.type) {
      case recordNotifications.sharedByMe:
        return "freigegeben am";
      case recordNotifications.sharedWithMe:
        return "freigegeben am";
      case recordNotifications.takenAwayFromMe:
        return "widerrufen am";
      case recordNotifications.revokedByMe:
        return "widerrufen am";
      case recordNotifications.completedFromMe:
        return "zurückgesendet am";
      case recordNotifications.returnedToMe:
        return "zurückerhalten am";
      case recordNotifications.editedFromMe:
        return "bearbeitet am";
      case recordNotifications.commented: {
        if (notification.acceptance === "accepted") return "Vorschlag akzeptiert am";
        else if (notification.acceptance === "declined") return "Vorschlag abgelehnt am";
        else return "kommentiert am";
      }
      // No default
    }
  }

  function confirmRevoke(notification) {
    refConfirmRevokeDialog.value.open(notification);
  }

  function confirmComplete(sharedrecordId) {
    refConfirmCompleteDialog.value.open(sharedrecordId);
  }

  defineExpose({
    open,
    close,
  });
</script>

<template>
  <ComponentDialog
    ref="refDialog"
    dialog-width="medium"
    :dialog-title="$page.props.can.view_shared_records ? 'Fallfreigabe und Zusammenarbeit' : 'Kommentare'"
    dialog-type="information"
    :test-id="getTestId('record-share-status')"
  >
    <template #content>
      <div class="flow-root">
        <div v-if="canCompleteRecord" class="mb-4 w-full border-b-2 pb-4">
          <p>
            Möchten Sie diese Variante an den ursprünglichen Absender
            <strong class="font-semibold whitespace-nowrap">{{ ownerName ?? ownerCompany }}</strong>
            <template v-if="ownerCompany && ownerName">&nbsp;({{ ownerCompany }})</template>
            zurücksenden?
          </p>

          <div v-if="currentSharedata.can_complete_this_record" class="mt-2">
            <MedTooltip
              v-if="
                currentSharedata.shared_owners &&
                currentSharedata.shared_owners[0] &&
                currentSharedata.shared_owners[0].id
              "
              :content="'Bearbeitung abschließen und ' + (ownerName ? 'an „' + ownerName + '“ ' : '') + 'zurücksenden'"
            >
              <MedButton
                variant="primary"
                icon="check"
                :data-test="getTestId('record-share-status-dialog-send-back-button')"
                @click="confirmComplete(currentSharedata.shared_owners[0].id)"
              >
                Zurücksenden
              </MedButton>
            </MedTooltip>
          </div>
        </div>

        <ul ref="refList" class="space-y-8 overflow-auto pr-4">
          <template v-if="currentNotifications.length > 0">
            <li
              v-for="(notification, key) in currentNotifications"
              :key="key"
              class="relative [&:last-child>span]:hidden"
            >
              <span class="absolute top-4 left-4 -ml-px h-full w-0.5 bg-gray-200" aria-hidden="true" />

              <div class="relative flex space-x-3">
                <div>
                  <span
                    class="flex size-8 items-center justify-center rounded-full ring-8 ring-white"
                    :class="{
                      'bg-gray-400':
                        notification.type !== recordNotifications.completedFromMe &&
                        notification.type !== recordNotifications.commented,
                      'bg-mcgreen-400': notification.type === recordNotifications.completedFromMe,
                      'bg-mcblue-400': notification.type === recordNotifications.commented,
                    }"
                  >
                    <template v-if="getActionIcon(notification).startsWith('custom:')">
                      <ComponentIcon
                        class="text-xl text-white"
                        :custom="getActionIcon(notification)"
                        :small-custom-icon="false"
                      />
                    </template>

                    <template v-else>
                      <ComponentIcon class="text-xl text-white">{{ getActionIcon(notification) }}</ComponentIcon>
                    </template>
                  </span>
                </div>

                <div class="flex min-w-0 flex-1 flex-col-reverse justify-between pt-1.5 sm:flex-row sm:space-x-4">
                  <div>
                    <!--                    <code class="text-xs">{{ notification }}</code>-->
                    <p class="text-sm text-gray-700">
                      {{ getIntroText(notification, true)[0] }}
                      <MedTooltip v-if="getAliasName(notification)" content="Alias-Name">
                        <strong class="font-bold text-gray-700">{{ getAliasName(notification) }}</strong>
                      </MedTooltip>

                      {{ getIntroText(notification, true)[1] }}
                    </p>

                    <div>
                      <template
                        v-if="notification.audience === 'group' && notification.type === recordNotifications.sharedByMe"
                      >
                        {{ notification.link }}
                      </template>

                      <template v-if="notification.audience === 'direct'">
                        <strong class="font-semibold">
                          {{ notification.partner_company || notification.partner_name }}
                        </strong>

                        <em
                          v-if="
                            notification.type === recordNotifications.commented &&
                            notification.confidential &&
                            !notification.partner_name
                          "
                        >
                          Extern hinzugezogener Arzt
                        </em>

                        <span v-if="notification.partner_company && notification.partner_name">
                          ({{ notification.partner_name }})
                        </span>

                        <span v-if="notification.type === recordNotifications.commented" class="text-sm text-gray-500">
                          <strong v-if="notification.acceptance === 'accepted'" class="font-bold text-mcgreen">
                            hat Vorschlag akzeptiert
                          </strong>

                          <strong v-else-if="notification.acceptance === 'declined'" class="font-bold text-mcred">
                            hat Vorschlag abgelehnt
                          </strong>

                          <span v-else>&nbsp;hat kommentiert</span>
                        </span>

                        <div
                          v-if="notification.type === recordNotifications.commented"
                          class="-mx-2 mt-1 w-96 rounded-lg bg-gray-100 p-2 text-sm italic"
                        >
                          {{ notification.content }}
                        </div>
                      </template>
                    </div>

                    <div class="mt-2">
                      <MedTooltip
                        v-if="
                          notification.status !== sharedrecordStatus.revoked &&
                          notification.type === recordNotifications.sharedByMe
                        "
                        content="Freigabe widerrufen"
                      >
                        <MedButton
                          variant="primary"
                          icon="person_add_disabled"
                          :data-test="getTestId('record-share-status-dialog-revoke-button')"
                          @click="confirmRevoke(notification)"
                        >
                          Widerrufen
                        </MedButton>
                      </MedTooltip>
                    </div>
                  </div>

                  <div class="text-sm text-gray-500 sm:text-right">
                    <MedTooltip :content="getVerbalizedActionText(notification)">
                      <time class="whitespace-nowrap">
                        {{ notification.created_at }}
                      </time>
                    </MedTooltip>
                  </div>
                </div>
              </div>
            </li>
          </template>

          <template
            v-if="
              currentSharedata.shared_with_me &&
              (currentNotifications.every((notification) => notification.type === recordNotifications.commented) ||
                currentNotifications.length === 0)
            "
          >
            <li class="relative [&:last-child>span]:hidden">
              <span class="absolute top-4 left-4 -ml-px h-full w-0.5 bg-gray-200" aria-hidden="true" />

              <div class="relative flex space-x-3">
                <div>
                  <span
                    class="flex size-8 items-center justify-center rounded-full ring-8 ring-white"
                    :class="{
                      'bg-gray-400': true,
                    }"
                  >
                    <ComponentIcon class="text-xl text-white">edit</ComponentIcon>
                  </span>
                </div>

                <div class="flex min-w-0 flex-1 justify-between space-x-4 pt-1.5">
                  <div>
                    <p class="text-sm text-gray-700">
                      Dies ist eine von Ihnen erstellte Variante auf Basis der ursprünglich mit Ihnen geteilten Analyse.
                    </p>
                    <p v-if="!currentSharedata.can_complete_this_record" class="mt-2 text-sm text-gray-700">
                      Ohne Analyseergebnis kann diese Variante nicht
                      <template v-if="ownername">an „{{ ownerName }}“</template>
                      zurückgesendet werden.
                    </p>
                  </div>

                  <div class="text-right text-sm text-gray-500" />
                </div>
              </div>
            </li>
          </template>
        </ul>

        <div
          v-if="
            currentNotifications.length === 0 &&
            (!currentSharedata.shared_owners || currentSharedata.shared_owners.length === 0)
          "
          class="space-y-4"
        >
          <p>
            Diese Medikationsanalyse wurde für Sie freigegeben und kann aufgrund eines Fehlers nicht zurückgesendet
            werden.
          </p>

          <p>
            In Ausnahmefällen kann es zu Problemen mit der Historie kommen, insb. wenn eine Medikationsanalyse vor
            langer Zeit freigegeben wurde.
          </p>

          <p>
            Sollte der Fehler wiederholt auftreten, wenden Sie sich bitte an den Support unter Angabe der folgenden
            Kennziffer:

            <code>origin-record={{ currentRecord.id }}</code>
          </p>
        </div>
      </div>
    </template>
  </ComponentDialog>

  <ComponentRecordShareRevokeDialog
    ref="refConfirmRevokeDialog"
    :patient="patient"
    :record="currentRecord"
    :test-id="getTestId('record-share-status')"
    @revoked="refresh()"
  />

  <ComponentRecordShareCompleteDialog
    ref="refConfirmCompleteDialog"
    :patient="patient"
    :record="currentRecord"
    :test-id="getTestId('record-share-status')"
    @completed="refresh()"
  />
</template>
